import themeUtils from '../themeUtils';
import StaticNavigation from './StaticNavigation';

export default class StaticHeader extends StaticNavigation {
  constructor(section) {
    super(section);

    this.el = section.el;

    this.mainHeader = this.el.querySelector('[data-header-main]');
    this.headerContent = this.el.querySelector('[data-header-content]');
    this.navigationWrapper = this.el.querySelector('[data-navigation-wrapper]');
    this.navigationContent = this.el.querySelector('[data-navigation-content]');
    this.branding = this.el.querySelector('[data-header-branding]');
    this.headerTools = this.el.querySelector('.header-tools');

    this.headerRight = this.el.querySelector('[data-header-content-right]');
    this.headerSearch = this.el.querySelector('[data-header-search]');
    this.headerSearchSubmit = this.el.querySelector('[data-header-search-button]');
    this.headerSearchClose = this.el.querySelector('[data-header-search-button-close]');

    this.cachedHeight = 0;
    this.cachedStickyHeight = 0;
    this.isCacheStale = true;

    this.isStickyHeader = section.data.stickyHeader;
    this.isCompactCenter = section.data.compactCenter === 'compact-center';
    this.navHeight = 0;
    this.windowWidth = themeUtils.windowWidth();
    setTimeout(() => {
      this.navHeight = this.navigationWrapper.getBoundingClientRect().height;
    },
    100);
    this._setSearchWidth();
    this._setupBranding();
    this._setCompactCenterHeights();
    this._toggleStickyHeader(this._shouldHeaderStick());
    this._bindEvents();
    this.reloadNavigation();
  }

  onSectionUnload() {
    super.onSectionUnload();
    window.removeEventListener('resize', this.resizeHeader);
    window.removeEventListener('scroll', this.scrollHeader);
    this.headerSearchSubmit.removeEventListener('click', this.clickHeaderSearchSubmit);
    this.headerSearchClose.removeEventListener('click', this.clickHeaderSearchClose);

    this._toggleStickyHeader(this._shouldHeaderStick());
  }

  _bindEvents() {
    this.resizeHeader = () => {
      // Prevent resize caused by iOS viewport change
      if (themeUtils.windowWidth() === this.windowWidth) { return; }
      this.windowWidth = themeUtils.windowWidth();

      this.navHeight = this.navigationWrapper.getBoundingClientRect().height;

      this.isCacheStale = true;

      this._toggleStickyHeader(this._shouldHeaderStick());
      this._setSearchWidth();
      this._setCompactCenterHeights();

      if (!themeUtils.isLessThanLarge()) {
        this._toggleSearchForm(false);
      }
    };
    this.scrollHeader = () => {
      // Only calculate heights on scroll if sticky header is disabled
      if (!this.isStickyHeader) {
        this._setCompactCenterHeights();
      }

      // Sticky header calculates heights
      this._toggleStickyHeader(this._shouldHeaderStick());
    };
    window.addEventListener('resize', this.resizeHeader);
    window.addEventListener('scroll', this.scrollHeader);

    this.clickHeaderSearchSubmit = event => {
      const { currentTarget } = event;

      if (!themeUtils.isLessThanLarge()) { return; }
      if (currentTarget.dataset.clicked) { return; }

      event.preventDefault();
      currentTarget.dataset.clicked = true;
      this._bindCloseSearch();
      this._toggleSearchForm(true);
    };
    this.clickHeaderSearchClose = event => {
      event.preventDefault();
      this._toggleSearchForm(false);
    };
    this.headerSearchSubmit.addEventListener('click', this.clickHeaderSearchSubmit);
    this.headerSearchClose.addEventListener('click', this.clickHeaderSearchClose);
  }

  /*
        When search form is open, and receives click outside of form, close
        */
  _bindCloseSearch() {
    this.clickHeaderSearchClose = event => {
      const { target } = event;
      const nodes = [];

      let element = target;
      nodes.push(element);
      while (element.parentElement) {
        if (element.parentElement.classList.contains('[data-header-search]')) {
          nodes.unshift(element.parentElement);
        }
        element = element.parentElement;
      }

      if (nodes.length) { return; }

      this._toggleSearchForm(false);
    };
    document.body.addEventListener('click', this.clickHeaderSearchClose);
  }

  _shouldHeaderStick() {
    let shouldStick = false;

    if (!this.isStickyHeader) {
      return shouldStick;
    }

    if (this.isCacheStale) {
      let height = document.body.classList.contains('sticky-header') ? 0 : this.headerContent.getBoundingClientRect().height;
      let stickyHeight = document.body.classList.contains('sticky-header') ? this.headerContent.getBoundingClientRect().height : 0;

      document.body.classList.toggle('sticky-header');

      height = !document.body.classList.contains('sticky-header') ? this.headerContent.getBoundingClientRect().height : height;
      stickyHeight = !document.body.classList.contains('sticky-header') ? stickyHeight : this.headerContent.getBoundingClientRect().height;

      document.body.classList.toggle('sticky-header');

      this.cachedHeight = height;
      this.cachedStickyHeight = stickyHeight;
      this.isCacheStale = false;
    }

    if (window.scrollTop > ((this.headerTools.getBoundingClientRect().height + this.cachedHeight) - this.cachedStickyHeight)) {
      shouldStick = true;
    }

    if (themeUtils.isLessThanLarge() || document.body.classList.contains('alternate-index-layout')) {
      shouldStick = false;
    }

    return shouldStick;
  }

  _toggleStickyHeader(toggleOn) {
    let paddingBottom;
    if (toggleOn == null) { toggleOn = false; }
    if (toggleOn && !document.body.classList.contains('sticky-header')) {
      paddingBottom = this.cachedHeight;
      this.mainHeader.style.paddingBottom = `${paddingBottom}px`;
    }

    if (!toggleOn) {
      paddingBottom = 0;
      this.mainHeader.style.paddingBottom = `${paddingBottom}px`;
    }

    if (toggleOn !== document.body.classList.contains('sticky-header')) {
      if (toggleOn) {
        document.body.classList.add('sticky-header');
      } else {
        document.body.classList.remove('sticky-header');
      }
      this._setCompactCenterHeights(!toggleOn);
    }
  }

  /*
        Expand search form logic
        */
  _toggleSearchForm(open) {
    if (open == null) { open = false; }
    let headerRightWidth = '';
    let headerSearchWidth = isNaN(this.headerSearch.dataset.width)
      ? this.headerSearch.data.width
      : parseInt(this.headerSearch.dataset.width, 10);
    const searchButtonWidth = this.headerSearchClose.getBoundingClientRect().width;

    let logoCollision = false;
    let navCollision = false;

    if (open) {
      ({ logoCollision, navCollision } = this._logoSearchCollision(headerSearchWidth));
      headerRightWidth = headerSearchWidth + searchButtonWidth;
      headerSearchWidth = '100%';
    } else {
      this.headerSearchSubmit.dataset.clicked = false;
      document.body.removeEventListener('click', this.clickHeaderSearchClose);
    }

    if (!open && this.mainHeader.classList.contains('header-search-expanded')) {
      this.mainHeader.classList.add('header-search-expanded-closing');
    }

    if (open) {
      this.mainHeader.classList.add('header-search-expanded');
    } else {
      this.mainHeader.classList.remove('header-search-expanded');
    }

    if (navCollision) {
      this.mainHeader.classList.add('header-nav-toggle-covered');
    } else {
      this.mainHeader.classList.remove('header-nav-toggle-covered');
    }

    this.mainHeader.addEventListener('transitionend', () => {
      if (!open) {
        this.mainHeader.classList.remove('header-search-expanded-closing');
      }
    });

    if (logoCollision) {
      this.mainHeader.classList.add('header-logo-covered');
    } else {
      this.mainHeader.classList.remove('header-logo-covered');
    }

    this.headerRight.style.width = isNaN(headerRightWidth) ? headerRightWidth : `${headerRightWidth}px`;
    this.headerSearch.style.width = isNaN(headerSearchWidth) ? headerSearchWidth : `${headerSearchWidth}px`;
  }

  /*
        Detect if Logo or nav toggle will be covered by expanded search form
        */
  _logoSearchCollision(headerSearchWidth) {
    const logoRightEdge = document.querySelector('[data-header-branding] .logo-link').getBoundingClientRect().right;
    const navRightEdge = document.querySelector('[data-header-content] [data-header-nav-toggle]').getBoundingClientRect().right;

    const searchLeftEdge = document.querySelector('[data-header-search]').getBoundingClientRect().left - headerSearchWidth;

    const logoCollision = logoRightEdge > searchLeftEdge;
    const navCollision = navRightEdge > searchLeftEdge;

    return {
      logoCollision,
      navCollision,
    };
  }

  /*
        Match Navigation and Branding heights
        Center navigation vertically if Branding is taller
        */
  _setCompactCenterHeights(toggleOn) {
    if (toggleOn == null) { toggleOn = true; }
    if (!this.isCompactCenter) { return; }

    let minHeight = 'auto';

    // Get height of inner navigation to prevent height locking on two row navigation
    const navHeight = this.navigationContent.getBoundingClientRect().height;
    let navOffset = 0;

    if (!themeUtils.isLessThanLarge() && toggleOn) {
      const heights = [];
      heights.push(navHeight);
      heights.push(this.branding.getBoundingClientRect().height);

      minHeight = Math.max.apply(null, heights);
    }

    if (navHeight < minHeight) {
      navOffset = (minHeight - navHeight) / 2;
    }

    this.navHeight = this.navigationWrapper.getBoundingClientRect().height;

    this.navigationWrapper.style.marginTop = `${navOffset}px`;
    this.headerContent.style.minHeight = `${minHeight}px`;
  }

  _setupBranding() {
    // Clone desktop branding to mobile
    const mobileBranding = this.branding.cloneNode(true);

    mobileBranding.removeAttribute('data-header-branding');
    mobileBranding.classList.remove('header-branding-desktop');
    mobileBranding.classList.add('header-branding-mobile');

    this.navigationContent.insertBefore(mobileBranding, this.navigationContent.firstChild);
  }

  _setSearchWidth() {
    const miniCartWrapper = this.mainHeader.querySelector('.mini-cart-wrapper');
    const checkoutLink = this.mainHeader.querySelector('.checkout-link');

    checkoutLink.style.display = 'inline-block';
    const searchWidth = (
      miniCartWrapper.getBoundingClientRect().width
      + checkoutLink.getBoundingClientRect().width
    );
    checkoutLink.style.display = '';

    this.headerSearch.style.width = `${searchWidth}px`;
    this.headerSearch.dataset.width = searchWidth;
  }
}
